/*
 * @providesModule WithdrawFlowController
 *
 */

import React from 'react';
import {NativeModules, Platform} from 'react-native';
import ClientPropertyConstants from 'ClientPropertyConstants';
import CurrentUserStore from 'CurrentUserStore';
import EmailManagement from 'EmailManagement';
import LoadingIndicator from 'LoadingIndicator';
import NavigationManager from 'NavigationManager';
import SKZPersonalInfo from 'SKZPersonalInfo';
import SMSVerification from 'SMSVerification';
import SMSVerifyController from 'SMSVerifyController';
import SkillzRouter from 'SkillzRouter';
import Withdrawal from 'Withdrawal';

const SkillzModule = NativeModules.SkillzModule;

const isWeb = Platform.OS === 'web';

/**
 * Bug 15 — "MIXED NAVIGATORS DETECTED … Target: EmailManagement".
 *
 * EmailManagement is on NavigationManager.web's MIGRATED_SCREENS list, so pushing the
 * component through the legacy stack logs a console warning (and `console.error` in
 * prod) and leaves the screen outside react-navigation's history. Its react-navigation
 * route already exists on web — src/app/(app)/games/[gid]/account/email, which renders
 * ManageEmailsScreen → EmailManagement through `withParams`. So on web we route there
 * with SkillzRouter, exactly as the warning instructs.
 *
 * `onEmailVerified` is deliberately not passed: it cannot survive the JSON round-trip
 * SkillzRouter does on screen params, and EmailManagement already falls back to
 * `WithdrawFlowController.displayWithdrawFromVerifiedEmail()` when the prop is absent —
 * i.e. the same continuation the pushed component supplied.
 *
 * Native keeps the legacy push/replace untouched.
 */
const WEB_EMAIL_MANAGEMENT_ROUTE = () =>
  `/games/${SkillzModule.App.GameId}/account/email`;

const WithdrawFlowController = {
  displayWithdrawFlow() {
    LoadingIndicator.show();
    this._showSMSPrompt((showPrompt: boolean) => {
      if (showPrompt) {
        this._displaySMSVerification();
      } else {
        LoadingIndicator.show();
        this._hasVerifiedAnEmail((success: boolean) => {
          if (success) {
            this._displayWithdrawScreen(false);
          } else {
            this._displayEmailManagementScreen();
          }
        });
      }
    });
  },

  _displayWithdrawFlowFromSMS() {
    this._hasVerifiedAnEmail((success: boolean) => {
      if (success) {
        this._displayWithdrawScreen(true);
      } else if (isWeb) {
        // Bug 15 (sibling call site): `replaceComponent` fires the same
        // MIXED NAVIGATORS warning for the migrated EmailManagement screen. This is
        // the post-SMS-verification leg of the same flow, so it replaces rather than
        // pushes — SkillzRouter.replace, as the warning instructs.
        SkillzRouter.replace(WEB_EMAIL_MANAGEMENT_ROUTE(), {fromWithdraw: true});
      } else {
        const EmailScreen = (
          <EmailManagement
            fromWithdraw={true}
            onEmailVerified={() => {
              WithdrawFlowController.displayWithdrawFromVerifiedEmail();
            }}
          />
        );
        NavigationManager.replaceComponent(EmailScreen, 'EmailManagement');
      }
    });
  },

  displayWithdrawFromVerifiedEmail() {
    LoadingIndicator.show();
    this._displayWithdrawScreen(true);
    LoadingIndicator.hide();
  },

  _hasVerifiedAnEmail(completion: (success: boolean) => {}): void {
    CurrentUserStore.fetchUserPersonalInfo(false)
      .then((personalInfo: Object) => {
        LoadingIndicator.hide();
        const info = new SKZPersonalInfo(personalInfo);
        LoadingIndicator.hide();
        completion(info.hasVerifiedAnEmail());
      })
      .catch((error: unknown) => {
        LoadingIndicator.hide();
        completion(false);
        console.debug('Error fetching user personal info on Withdraw flow controller: ', error);
      });
  },

  _showSMSPrompt(completion: (success: boolean) => {}): void {
    CurrentUserStore.fetchUserPersonalInfo(false)
      .then(async (personalInfo: Object) => {
        const info = new SKZPersonalInfo(personalInfo);
        if (!info.phone_verified) {
          const shouldShowPrompt = await SMSVerifyController.showPrompt(
            ClientPropertyConstants.getSMSPromptWithdraw()
          );
          LoadingIndicator.hide();
          completion(shouldShowPrompt);
        } else {
          LoadingIndicator.hide();
          completion(false);
        }
      })
      .catch((error: unknown) => {
        LoadingIndicator.hide();
        completion(false);
        console.debug('Error fetching user personal info on Withdraw flow controller: ', error);
      });
  },

  _displayEmailManagementScreen() {
    // Bug 15: this legacy push is what logged "MIXED NAVIGATORS DETECTED; Action:
    // pushComponentWithScene; Target: EmailManagement" when Withdraw was opened from
    // the side menu. See WEB_EMAIL_MANAGEMENT_ROUTE above.
    if (isWeb) {
      SkillzRouter.push(WEB_EMAIL_MANAGEMENT_ROUTE(), {fromWithdraw: true});
      return;
    }
    const EmailScreen = (
      <EmailManagement
        fromWithdraw={true}
        onEmailVerified={() => {
          WithdrawFlowController.displayWithdrawFromVerifiedEmail();
        }}
      />
    );
    NavigationManager.pushComponentWithScene(EmailScreen, true, 'EmailManagement');
  },

  _displaySMSVerification() {
    const component = (
      <SMSVerification
        onClose={(): void => NavigationManager.pop()}
        showCloseButton={SMSVerifyController.showCloseButton(
          ClientPropertyConstants.getSMSPromptWithdraw()
        )}
        onDone={(): void => this._displayWithdrawFlowFromSMS()}
        clientProps={{
          header: ClientPropertyConstants.getSMSPromptWithdrawHeader(),
          body: ClientPropertyConstants.getSMSPromptWithdrawBody(),
        }}
      />
    );
    NavigationManager.pushComponentWithScene(component, false, 'SMSVerify');
  },

  _displayWithdrawScreen(shouldReplace: boolean = false) {
    const WithdrawalScreen = <Withdrawal />;
    const gameId = SkillzModule.App.GameId;
    // FIXME: This is a temporary fix to handle the web and mobile different navigation behavior to solve a bug on the SDK side.
    if (shouldReplace) {
      isWeb
        ? SkillzRouter.replace(`/games/${gameId}/account/withdraw`)
        : NavigationManager.replaceComponent(WithdrawalScreen, 'Withdraw');
    } else {
      isWeb
        ? SkillzRouter.push(`/games/${gameId}/account/withdraw`)
        : NavigationManager.pushComponentWithScene(WithdrawalScreen, true, 'Withdraw');
    }
  },
};

export {WithdrawFlowController as default};
